import React, { useState, useEffect, useReducer, useCallback, useMemo } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { parseISO } from 'date-fns';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageTitle from '../../../layouts/PageTitle';
import swal from 'sweetalert';
import { toast } from 'sonner';
import { formatISO } from 'date-fns';

// Modern components
import InvoiceActionsBootstrap from './newinvoiceform/InvoiceActionsBootstrap';
import CreditNoteFormBootstrap from './newinvoiceform/CreditNoteFormBootstrap';
import TemplateSelectorBootstrap from './newinvoiceform/TemplateSelectorBootstrap';
import CustomerDisplayCard from './CustomerDisplayCard.jsx';
import DeliveryOptionsCard from './newinvoiceform/DeliveryOptionsCard.jsx';
import PaymentMethodsCard from './newinvoiceform/PaymentMethodsCard.jsx';
import NewArticleModal from '../Factoring/Articles/NewArticleModal';

// Reducers and hooks
import {
  creditNoteReducer,
  createInitialCreditNoteState,
  CREDIT_NOTE_ACTIONS
} from './reducers/creditNoteReducers';
import { useChangeTracking } from './hooks/useChangeTracking';
import { useInvoiceSettings } from '../../../../context/InvoiceSettingsContext';

const normalizeItemsForComparison = (items) => {
  return items.map(item => ({
    id: item.id || item.item_id,
    productcode: item.productcode || '',
    description: item.description || '',
    unitprice: Number(item.unitprice) || 0,
    quantity: Number(item.quantity) || 0,
    original_quantity: Number(item.original_quantity) || 0,
    unit: item.unit || 'st',
    tax_rate: item.tax_rate || '25%',
    discount: Number(item.discount) || 0,
    type: item.type || 'Product'
  }));
};

const CreditNoteForm = () => {
  const { companyId, invoiceId } = useParams();
  const history = useHistory();
  const { invoiceSettings } = useInvoiceSettings();

  // Don't determine mode until we fetch the invoice data
  const [isEditMode, setIsEditMode] = useState(null); // null = unknown, true = edit, false = create

  // Initialize state with reducer - pass null for editMode until we know
  const [state, dispatch] = useReducer(
    creditNoteReducer,
    createInitialCreditNoteState(invoiceId, false, invoiceSettings) // Always start as create mode
  );

  // Additional state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [creditNoteId, setCreditNoteId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Article modal state
  const [showNewArticleModal, setShowNewArticleModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // REPLACE the custom hook with direct tracked data definition (same as InvoiceForm)
  const trackedData = useMemo(() => ({
    creditNoteData: {
      customerid: state.customerid || '',
      customer: state.customer ? {
        id: state.customer.id,
        name: state.customer.name || state.customer.customername,
        email: state.customer.email
      } : null,
      creditNoteNumber: state.creditNoteNumber || '',
      creditNoteDate: state.creditNoteDate?.getTime() || null,
      dueDate: state.dueDate?.getTime() || null,
      currency: state.currency || 'SEK',
      roundTotal: Boolean(state.roundTotal),
      notes: state.notes || '',
      status: state.status || 'Draft'
    },
    items: normalizeItemsForComparison(state.items || []),
    deliveryOptions: {
      method: state.deliveryOptions?.method || 'download',
      email: state.deliveryOptions?.email || '',
      phone: state.deliveryOptions?.phone || '',
      message: state.deliveryOptions?.message || '',
      subject: state.deliveryOptions?.subject || ''
    },
    paymentMethods: state.paymentMethods || {}
  }), [
    state.customerid,
    state.customer?.id,
    state.customer?.name,
    state.customer?.customername,
    state.customer?.email,
    state.creditNoteNumber,
    state.creditNoteDate?.getTime(),
    state.dueDate?.getTime(),
    state.currency,
    state.roundTotal,
    state.notes,
    state.status,
    state.items,
    state.deliveryOptions?.method,
    state.deliveryOptions?.email,
    state.deliveryOptions?.phone,
    state.deliveryOptions?.message,
    state.deliveryOptions?.subject,
    state.paymentMethods
  ]);

  // Use the base changeTracking hook directly (same as InvoiceForm)
  const { hasChanges, resetTracking, getChangedFields } = useChangeTracking(
    isInitialized ? trackedData : null
  );

  console.log('Credit Note tracked data:', getChangedFields());
  console.log('Credit Note State - Customer:', state.customer);
  console.log('Credit Note State - Loading:', loading);
  console.log('Credit Note State - IsEditMode:', isEditMode);

  // Form validation
  const isFormValid = useMemo(() => {
    if (!state.customer) return false;

    // Check if any items are selected for credit
    const hasItemsToCredit = state.items.some(item =>
      item.type !== 'Text' && item.quantity > 0
    );

    if (!hasItemsToCredit) return false;

    // Check if any quantities exceed original
    const invalidQuantities = state.items.filter(item =>
      item.type !== 'Text' && item.quantity > item.original_quantity
    );

    return invalidQuantities.length === 0;
  }, [state.customer, state.items]);

  // Initialize component
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchArticles(),
        fetchCustomers(),
        determineMode()
      ]);

      setTimeout(() => {
        setIsInitialized(true);
      }, 500);
    };

    loadInitialData();
  }, []);

  // NEW: Determine if we're in edit or create mode
  const determineMode = async () => {
    if (!invoiceId) {
      setError('Inget faktura-ID angivet');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        getApiUrl(`${companyId}/invoices/${invoiceId}/details`),
        { withCredentials: true }
      );

      const invoiceData = response.data.data;
      console.log('Fetched invoice details to determine mode:', invoiceData);

      if (invoiceData.invoice_type === 'credit_note') {
        // Edit mode - we're editing an existing credit note
        console.log('Edit mode: Editing existing credit note');
        setIsEditMode(true);
        setCreditNoteId(invoiceId);
        await loadExistingCreditNote(invoiceData);
      } else {
        // Create mode - we're creating a credit note based on an existing invoice
        console.log('Create mode: Creating credit note from invoice');
        setIsEditMode(false);
        await loadOriginalInvoiceForCredit(invoiceData);
      }
    } catch (error) {
      console.error('Error determining mode:', error);
      setError('Kunde inte läsa in fakturainformation');
    } finally {
      setLoading(false);
    }
  };

  // Load existing credit note for editing
  const loadExistingCreditNote = async (creditNoteData) => {
    try {
      // Load original invoice if available
      if (creditNoteData.related_invoice_id) {
        const originalResponse = await axios.get(
          getApiUrl(`${companyId}/invoices/${creditNoteData.related_invoice_id}/details`),
          { withCredentials: true }
        );
        dispatch({
          type: CREDIT_NOTE_ACTIONS.LOAD_ORIGINAL_INVOICE,
          payload: originalResponse.data.data
        });
      }

      // Parse and load credit note data
      const parsedCreditNote = {
        ...creditNoteData,
        creditNoteDate: creditNoteData.invoicedate ? parseISO(creditNoteData.invoicedate) : new Date(),
        dueDate: creditNoteData.deadline ? parseISO(creditNoteData.deadline) : new Date(),
        items: creditNoteData.items?.map(item => ({
          ...item,
          quantity: Math.abs(item.quantity),
          original_quantity: item.original_quantity || Math.abs(item.quantity)
        })) || []
      };

      dispatch({
        type: CREDIT_NOTE_ACTIONS.LOAD_CREDIT_NOTE,
        payload: parsedCreditNote
      });

      // Explicitly set customer data if it exists
      if (creditNoteData.customerid) {
        const customerDetails = await fetchCustomerDetails(creditNoteData.customerid);
        if (customerDetails) {
          dispatch({
            type: CREDIT_NOTE_ACTIONS.SET_CUSTOMER,
            payload: customerDetails
          });
        }
      }

      console.log('Loaded existing credit note:', parsedCreditNote);

      setTimeout(() => {
        resetTracking();
      }, 200);

    } catch (error) {
      console.error('Error loading existing credit note:', error);
      setError('Kunde inte ladda befintlig kreditfaktura');
    }
  };

  // Load original invoice for credit note creation
  const loadOriginalInvoiceForCredit = async (invoiceData) => {
    try {
      dispatch({
        type: CREDIT_NOTE_ACTIONS.LOAD_ORIGINAL_INVOICE,
        payload: invoiceData
      });

      // Explicitly set customer data from invoice
      if (invoiceData.customerid) {
        const customerDetails = await fetchCustomerDetails(invoiceData.customerid);
        if (customerDetails) {
          dispatch({
            type: CREDIT_NOTE_ACTIONS.SET_CUSTOMER,
            payload: customerDetails
          });
        }
      }

      console.log('Loaded original invoice for credit:', invoiceData);

      // Auto-populate delivery options with customer info from invoice if available
      if (invoiceData) {
        const customerEmail = invoiceData.email || '';
        const customerPhone = invoiceData.phone || '';

        // Update delivery options with customer contact info
        setTimeout(() => {
          dispatch({
            type: CREDIT_NOTE_ACTIONS.UPDATE_DELIVERY_OPTIONS,
            payload: {
              email: customerEmail,
              phone: customerPhone,
              method: 'download',
              message: '',
              subject: 'Kreditfaktura'
            }
          });
        }, 100);

        console.log('Auto-populated delivery options from original invoice:', {
          email: customerEmail,
          phone: customerPhone
        });
      }

      // Reset tracking after loading data to prevent "unsaved changes" warning
      setTimeout(() => {
        resetTracking();
      }, 200);
    } catch (error) {
      console.error('Error loading original invoice:', error);
      setError('Kunde inte ladda originalfaktura');
    }
  };

  // Fetch functions (keep existing)
  const fetchArticles = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/articles`),
        { withCredentials: true }
      );
      setArticles(response.data.data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/sales/customers`),
        { withCredentials: true }
      );
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      console.log('Fetching customer details for:', customerId);
      const response = await axios.get(
        getApiUrl(`${companyId}/sales/customers/${customerId}`),
        { withCredentials: true }
      );
      console.log('Customer details response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer details:', error);
      return null;
    }
  };

  // Form handlers (keep existing)
  const handleFormDataChange = useCallback((updatedData) => {
    if ('customer' in updatedData) {
      dispatch({
        type: CREDIT_NOTE_ACTIONS.SET_CUSTOMER,
        payload: updatedData.customer
      });

      // Auto-populate delivery options with customer contact information
      if (updatedData.customer) {
        const customerEmail = updatedData.customer.email ||
          (updatedData.customer.contacts && updatedData.customer.contacts.length > 0
            ? updatedData.customer.contacts[0].contact_email
            : '');

        const customerPhone = updatedData.customer.phone ||
          (updatedData.customer.contacts && updatedData.customer.contacts.length > 0
            ? updatedData.customer.contacts[0].contact_phone
            : '');

        // Use setTimeout to ensure customer is set before updating delivery options
        setTimeout(() => {
          dispatch({
            type: CREDIT_NOTE_ACTIONS.UPDATE_DELIVERY_OPTIONS,
            payload: {
              email: customerEmail,
              phone: customerPhone,
              method: state.deliveryOptions?.method || 'download',
              message: state.deliveryOptions?.message || '',
              subject: state.deliveryOptions?.subject || 'Kreditfaktura'
            }
          });
        }, 50);

        console.log('Auto-populated delivery options for credit note:', {
          email: customerEmail,
          phone: customerPhone
        });
      }
    }

    // Handle other form field updates
    Object.entries(updatedData).forEach(([field, value]) => {
      if (field !== 'customer') {
        dispatch({
          type: CREDIT_NOTE_ACTIONS.UPDATE_CREDIT_NOTE_DATA,
          payload: { [field]: value }
        });
      }
    });
  }, [state.deliveryOptions]);

  const handleDeliveryMethodChange = useCallback((method) => {
    dispatch({
      type: CREDIT_NOTE_ACTIONS.UPDATE_DELIVERY_OPTIONS,
      payload: { method }
    });
  }, []);

  const handleDeliveryDetailsChange = useCallback((details) => {
    dispatch({
      type: CREDIT_NOTE_ACTIONS.UPDATE_DELIVERY_OPTIONS,
      payload: details
    });
  }, []);

  const handlePaymentMethodChange = useCallback((method) => {
    dispatch({
      type: CREDIT_NOTE_ACTIONS.TOGGLE_PAYMENT_METHOD,
      payload: method
    });
  }, []);

  // Save and publish handlers - UPDATE the API endpoints based on mode
  const handleSaveDraft = useCallback(async () => {

    console.log('Saving draft...');

    if (!isFormValid) {
      toast.warning('Varning', {
        description: 'Vänligen kontrollera att alla obligatoriska fält är ifyllda'
      });
      return null;
    }

    try {
      const dataToSend = {
        customerid: state.customerid,
        items: state.items,
        notes: state.notes,
        currency: state.currency,
        credit_note_date: formatISO(state.creditNoteDate, { representation: 'date' }),
        due_date: formatISO(state.dueDate, { representation: 'date' }),
        rounding: !state.roundTotal,
        status: 'Draft',
        original_invoice_id: state.originalInvoiceId,
        delivery_options: state.deliveryOptions,
        payment_methods: Object.keys(state.paymentMethods).filter(m => state.paymentMethods[m])
      };

      let response;
      if (isEditMode && creditNoteId) {
        // Update existing credit note
        response = await axios.put(
          getApiUrl(`${companyId}/invoices/credit-notes/${creditNoteId}`),
          dataToSend,
          { withCredentials: true }
        );
      } else {
        // Create new credit note from original invoice
        response = await axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/credit-note`),
          dataToSend,
          { withCredentials: true }
        );
      }

      if (response.data.success) {
        const newCreditNoteId = response.data.creditNoteId || creditNoteId;
        setCreditNoteId(newCreditNoteId);

        setTimeout(() => {
          resetTracking();
        }, 100);

        toast.success('Klart', {
          description: 'Kreditfaktura sparad'
        });
        return newCreditNoteId;
      }
    } catch (error) {
      console.error('Error saving credit note:', error);
      toast.error('Fel', {
        description: 'Kunde inte spara kreditfakturan'
      });
      return null;
    }
  }, [state, isFormValid, isEditMode, creditNoteId, invoiceId, resetTracking]);

  // Keep all other handlers the same...
  const handlePublish = useCallback(async () => {
    if (!isFormValid) {
      toast.warning('Varning', {
        description: 'Vänligen kontrollera att alla obligatoriska fält är ifyllda'
      });
      return;
    }

    // Save first if needed
    let creditNoteIdToPublish = creditNoteId;
    if (!creditNoteId || hasChanges) {
      creditNoteIdToPublish = await handleSaveDraft();
      if (!creditNoteIdToPublish) return;
    }

    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/invoices/credit-notes/${creditNoteIdToPublish}/publish`),
        {
          action: state.deliveryOptions.method,
          email: state.deliveryOptions.method === 'email' ? state.deliveryOptions.email : undefined
        },
        {
          withCredentials: true,
          responseType: state.deliveryOptions.method === 'download' ? 'blob' : 'json'
        }
      );

      if (state.deliveryOptions.method === 'download' && response.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `credit-note-${creditNoteIdToPublish}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      }

      toast.success('Klart', {
        description: 'Kreditfaktura publicerad'
      });
      history.push(`/${companyId}/invoices`);
    } catch (error) {
      console.error('Error publishing credit note:', error);
      toast.error('Fel', {
        description: 'Kunde inte publicera kreditfakturan'
      });
    }
  }, [state, isFormValid, creditNoteId, hasChanges, handleSaveDraft, history]);

  // Article management
  const handleNewArticle = (articleData) => {
    setSelectedArticle(articleData);
    setShowNewArticleModal(true);
  };

  const handleSaveArticle = async (newArticle) => {
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/articles`),
        newArticle,
        { withCredentials: true }
      );
      if (response.data.success) {
        await fetchArticles();
        toast.success('Klart', {
          description: 'Artikeln sparades'
        });
      }
    } catch (error) {
      toast.error('Fel', {
        description: 'Kunde inte spara artikeln'
      });
    }
    setShowNewArticleModal(false);
  };

  // Delete draft handler for credit notes
  const handleDeleteDraft = useCallback(async () => {
    if (!creditNoteId || state.status !== 'Draft') return;
    const confirm = await swal({
      title: 'Radera utkast?',
      text: 'Vill du verkligen radera detta kreditfaktura-utkast? Detta går inte att ångra.',
      icon: 'warning',
      buttons: [true, 'Radera'],
      dangerMode: true
    });
    if (!confirm) return;
    try {
      await axios.delete(getApiUrl(`${companyId}/credit-notes/${creditNoteId}`), { withCredentials: true });
      toast.success('Borttagen', {
        description: 'Kreditfaktura-utkastet har raderats'
      });
      history.push(`/${companyId}/invoices`);
    } catch (error) {
      console.error('Error deleting credit note draft:', error);
      toast.error('Fel', {
        description: 'Kunde inte radera kreditfaktura-utkastet'
      });
    }
  }, [creditNoteId, companyId, state.status, history]);

  // Navigation protection
  useEffect(() => {
    const unblock = history.block((location) => {
      if (hasChanges) {
        swal({
          title: 'Du har osparade ändringar!',
          text: 'Vill du spara utkastet innan du lämnar sidan?',
          icon: 'warning',
          buttons: {
            cancel: 'Avbryt',
            discard: {
              text: 'Fortsätt utan att spara',
              value: 'discard',
            },
            save: {
              text: 'Spara utkast',
              value: 'save',
            },
          },
        }).then((value) => {
          switch (value) {
            case 'save':
              handleSaveDraft().then(() => {
                unblock();
                history.push(location.pathname);
              });
              break;
            case 'discard':
              unblock();
              history.push(location.pathname);
              break;
            default:
              break;
          }
        });
        return false;
      }
      return true;
    });

    return () => unblock();
  }, [hasChanges, history, handleSaveDraft]);

  // Loading state - wait until we know the mode
  if (loading || isEditMode === null) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="d-flex flex-column h-100">
        <PageTitle
          activeMenu={isEditMode ? "Redigera Kreditfaktura" : "Skapa Kreditfaktura"}
          motherMenu="Fakturor"
        />

        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                {isEditMode ? 'Redigera Kreditfaktura' : 'Skapa Kreditfaktura'}
                {state.status && (
                  <span className={`badge ms-3 ${state.status === 'Draft' ? 'bg-warning' : 'bg-success'}`}>
                    {state.status === 'Draft' ? 'Utkast' : 'Skickad'}
                  </span>
                )}
                {isEditMode && state.creditNoteNumber && (
                  <span className="badge bg-info ms-2">#{state.creditNoteNumber}</span>
                )}
                {state.originalInvoice && (
                  <span className="badge bg-secondary ms-2">
                    Krediterar #{state.originalInvoice.invoicenumber}
                  </span>
                )}
              </h4>
              <p className="text-muted mb-0">
                {isEditMode
                  ? 'Uppdatera kreditfakturan och justera antal vid behov'
                  : 'Välj vilka artiklar som ska krediteras från originalfakturan'
                }
                {hasChanges && isInitialized && <span className="text-warning ms-2">(Osparade ändringar)</span>}
              </p>
            </div>

            <div className="d-flex gap-2">
              <Link
                to={`/${companyId}/invoices`}
                className="btn btn-outline-secondary"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Tillbaka
              </Link>
            </div>
          </div>
        </div>

        {/* Actions Section */}
        <InvoiceActionsBootstrap
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          isValid={isFormValid}
          hasUnsavedChanges={hasChanges && isInitialized}
          loading={loading}
          isCreditNote={true}
          onDeleteDraft={isEditMode && state.status === 'Draft' ? handleDeleteDraft : undefined}
        />

        {/* Customer Display Section - Read-only for credit notes */}
        {state.customer && (
          <CustomerDisplayCard
            selectedCustomer={state.customer}
            loading={loading}
          />
        )}

        {/* Template Selection */}
        {state.customer && (
          <div className="mb-4">
            <TemplateSelectorBootstrap
              selectedTemplate={state.selectedTemplate}
              onSelectTemplate={(template) => dispatch({ type: CREDIT_NOTE_ACTIONS.SET_TEMPLATE, payload: template })}
              availableTemplates={invoiceSettings?.templates}
            />
          </div>
        )}

        {/* Delivery Options */}
        {state.customer && (
          <DeliveryOptionsCard
            deliveryMethod={state.deliveryOptions?.method || 'download'}
            onDeliveryMethodChange={handleDeliveryMethodChange}
            deliveryDetails={state.deliveryOptions}
            onDeliveryDetailsChange={handleDeliveryDetailsChange}
            invoiceSettings={invoiceSettings}
          />
        )}

        {/* Payment Methods */}
        {state.customer && (
          <PaymentMethodsCard
            paymentMethods={state.paymentMethods || {}}
            onPaymentMethodChange={handlePaymentMethodChange}
            invoiceSettings={invoiceSettings}
          />
        )}

        {/* Main Form Content */}
        {state.customer && (
          <CreditNoteFormBootstrap
            formData={{
              customerid: state.customerid,
              customer: state.customer,
              creditNoteNumber: state.creditNoteNumber,
              creditNoteDate: state.creditNoteDate,
              dueDate: state.dueDate,
              currency: state.currency,
              roundTotal: state.roundTotal,
              items: state.items,
              notes: state.notes,
              originalInvoice: state.originalInvoice
            }}
            onFormDataChange={handleFormDataChange}
            availableItems={articles}
            loading={loading}
            invoiceSettings={invoiceSettings}
            validationErrors={validationErrors}
            isEditMode={isEditMode}
            hasOriginalInvoice={!!state.originalInvoice}
          />
        )}

        <NewArticleModal
          show={showNewArticleModal}
          handleClose={() => setShowNewArticleModal(false)}
          handleSave={handleSaveArticle}
          articleDetails={selectedArticle}
        />
      </div>
    </>
  );
};

export default CreditNoteForm;
